import {
  createBrowserRouter,
  createRoutesFromElements,
  Outlet,
  Route,
} from "react-router";
import { RouterProvider } from "react-router/dom";
import "core-js/proposals/promise-with-resolvers";

import Landing from "@layouts/Landing/Landing";
import UserDotCom from "@layouts/UserDotCom/UserDotCom";
import ErrorBoundaryRoute from "@routes/ErrorBoundaryRoute/ErrorBoundaryRoute";
import ErrorBoundaryEmptyState from "@Core/components/emptyStates/Error503/ErrorBoundaryEmptyState";
import BootScreen from "@routes/BootScreen";
import {
  DIETITIAN_ROLES,
  protectedLoader,
  revalidateOnPathChange,
} from "@routes/protectedLoader";
import BetaLockRoute from "@routes/BetaLockRoute";
import {
  ACTIVATION,
  AUTH,
  LINK_RESEND,
  MAINTENANCE,
  PUBLIC_VISIT,
  QUESTIONNAIRE,
  ONBOARDING,
  REGISTER_PATIENT,
  RETRIEVE,
  VISIT_CANCEL,
  VISIT_CONFIRM,
} from "@routes/routes";
import { authRoutes } from "@routes/authRoutes";
import {
  DashboardLayout,
  ActivationPage,
  LinkResendPage,
  QuestionnaireAnswerPage,
  AiOnboardingPage,
  RecoveryPage,
  RegisterPatientPage,
  VisitCancelPage,
  VisitConfirmPage,
} from "@routes/lazyPages";
import { coreRoutes } from "@routes/domains/coreRoutes";
import { clientRoutes } from "@routes/domains/clientRoutes";
import { programRoutes } from "@routes/domains/programRoutes";
import { patientProgramRoutes } from "@routes/domains/patientProgramRoutes";
import { dietRoutes } from "@routes/domains/dietRoutes";
import { mealRoutes } from "@routes/domains/mealRoutes";
import { recipeRoutes } from "@routes/domains/recipeRoutes";
import { productRoutes } from "@routes/domains/productRoutes";
import { sectionRoutes } from "@routes/domains/sectionRoutes";
import { settingsRoutes } from "@routes/domains/settingsRoutes";
import { questionnaireRoutes } from "@routes/domains/questionnaireRoutes";
import { taskRoutes } from "@routes/domains/taskRoutes";
import { ownerRoutes } from "@routes/domains/ownerRoutes";
import { salesRoutes } from "@routes/domains/salesRoutes";
import { purchaseRoutes } from "@routes/domains/purchaseRoutes";

const routes = createRoutesFromElements(
  <Route element={<UserDotCom />} HydrateFallback={BootScreen}>
    {/** public access routes */}

    <Route path={MAINTENANCE} element={<ErrorBoundaryRoute defaultOpen />} />
    {authRoutes}
    <Route
      path={AUTH}
      element={
        <ErrorBoundaryRoute>
          <Landing authPage />
        </ErrorBoundaryRoute>
      }
    >
      <Route path={ACTIVATION} lazy={ActivationPage} />
      <Route path={LINK_RESEND} lazy={LinkResendPage} />
      <Route path={RETRIEVE} lazy={RecoveryPage} />
      <Route path={REGISTER_PATIENT} lazy={RegisterPatientPage} />
    </Route>
    <Route
      path={PUBLIC_VISIT}
      element={
        <ErrorBoundaryRoute>
          <Landing />
        </ErrorBoundaryRoute>
      }
    >
      <Route path={VISIT_CONFIRM} lazy={VisitConfirmPage} />
      <Route path={VISIT_CANCEL} lazy={VisitCancelPage} />
    </Route>

    <Route
      path={QUESTIONNAIRE}
      element={
        <ErrorBoundaryRoute>
          <Outlet />
        </ErrorBoundaryRoute>
      }
      errorElement={<ErrorBoundaryEmptyState />}
    >
      <Route index lazy={QuestionnaireAnswerPage} />
    </Route>

    <Route
      loader={protectedLoader(DIETITIAN_ROLES)}
      shouldRevalidate={revalidateOnPathChange}
      element={
        <ErrorBoundaryRoute>
          <Outlet />
        </ErrorBoundaryRoute>
      }
      errorElement={<ErrorBoundaryEmptyState />}
    >
      <Route element={<BetaLockRoute />}>
        <Route path={ONBOARDING} lazy={AiOnboardingPage} />
      </Route>
    </Route>

    <Route
      loader={protectedLoader(DIETITIAN_ROLES)}
      shouldRevalidate={revalidateOnPathChange}
      element={
        <ErrorBoundaryRoute>
          <Outlet />
        </ErrorBoundaryRoute>
      }
      errorElement={<ErrorBoundaryEmptyState />}
    >
      <Route lazy={DashboardLayout}>
        <Route element={<BetaLockRoute />}>
          {coreRoutes}
          {clientRoutes}
          {programRoutes}
          {patientProgramRoutes}
          {dietRoutes}
          {mealRoutes}
          {recipeRoutes}
          {productRoutes}
          {sectionRoutes}
          {settingsRoutes}
          {questionnaireRoutes}
          {taskRoutes}
          {purchaseRoutes}
        </Route>
      </Route>
    </Route>
    {ownerRoutes}
    {salesRoutes}
  </Route>,
);

let router: ReturnType<typeof createBrowserRouter> | undefined;

export function Routes() {
  router ??= createBrowserRouter(routes);
  return <RouterProvider router={router} />;
}

export default Routes;
